import {
  onIdTokenChanged,
  signInWithPopup,
  signOut,
  type User
} from "firebase/auth";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from "react";
import { auth, googleProvider } from "./firebase";

type AdminSession = {
  user: User | null;
  token: string | null;
  isAdmin: boolean;
  isLoading: boolean;
  signIn: () => Promise<void>;
  signOutAdmin: () => Promise<void>;
  refreshToken: () => Promise<string | null>;
};

const AuthContext = createContext<AdminSession | null>(null);

function hasAdminClaim(claims: Record<string, unknown>) {
  const roles = claims.roles as Record<string, unknown> | undefined;
  return Boolean(claims.admin || roles?.admin);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const refreshToken = useCallback(async () => {
    if (!auth.currentUser) return null;

    const [nextToken, tokenResult] = await Promise.all([
      auth.currentUser.getIdToken(true),
      auth.currentUser.getIdTokenResult(true)
    ]);

    setToken(nextToken);
    setIsAdmin(hasAdminClaim(tokenResult.claims));
    return nextToken;
  }, []);

  useEffect(() => {
    return onIdTokenChanged(auth, async (nextUser) => {
      setUser(nextUser);

      if (!nextUser) {
        setToken(null);
        setIsAdmin(false);
        setIsLoading(false);
        return;
      }

      const [nextToken, tokenResult] = await Promise.all([
        nextUser.getIdToken(),
        nextUser.getIdTokenResult()
      ]);

      setToken(nextToken);
      setIsAdmin(hasAdminClaim(tokenResult.claims));
      setIsLoading(false);
    });
  }, []);

  const value = useMemo<AdminSession>(() => ({
    user,
    token,
    isAdmin,
    isLoading,
    signIn: async () => {
      await signInWithPopup(auth, googleProvider);
    },
    signOutAdmin: async () => {
      await signOut(auth);
    },
    refreshToken
  }), [isAdmin, isLoading, refreshToken, token, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAdminAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAdminAuth must be used inside AuthProvider");
  }

  return context;
}
